import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ContextService } from '@maximizer/core/shared/data-access';
import {
  ActivityTimelineUpcomingOverdue,
  EntryType,
  Octopus,
} from '@maximizer/core/shared/domain';
import { map, Observable } from 'rxjs';
import { BaseTimelineService } from './base-timeline-service';
import { TimelineFilter, TimelineKeys } from '../state/timeline.models';
import { isObject } from '@maximizer/shared/util';

@Injectable()
export class ActivityTimelineService extends BaseTimelineService {
  readonly fields: Octopus.Scope<Octopus.ActivityTimeline> = {
    '$LEFT(Description, 200)': 1,
    '$LEFT(Subject, 200)': 1,
    AbEntries: [
      {
        Key: 1,
        DisplayValue: 1,
      },
    ],
    Completed: 1,
    Creator: {
      Value: 1,
      DisplayValue: 1,
    },
    Key: 1,
    Leads: [
      {
        Key: 1,
        DisplayValue: 1,
      },
    ],
    RecurrenceKey: 1,
    SecStatus: {
      CanRead: 1,
      CanDelete: 1,
      CanUpdate: 1,
    },
    StartDate: {
      Value: 1,
      DisplayValue: 1,
    },
    Type: {
      Icon: 1,
      Key: 1,
      DisplayValue: 1,
      External: 1,
    },
    Users: [
      {
        Key: {
          Uid: 1,
          Value: 1,
        },
        DisplayValue: 1,
      },
    ],
    With: {
      Opportunity: {
        Key: 1,
        Objective: 1,
      },
      Case: {
        Key: 1,
        CaseNumber: 1,
        Subject: 1,
      },
    },
    WithKey: 1,
    InteractionCategory: {
      Value: 1,
      DisplayValue: 1,
    },
    WorkflowInstanceKey: 1,
  };

  constructor(
    private readonly http: HttpClient,
    private readonly context: ContextService,
  ) {
    super();
  }

  getOverdueAndUpcomingByKey(
    key: string,
  ): Observable<ActivityTimelineUpcomingOverdue> {
    const overdueRequest: Octopus.ActivityOverdueReadRequest = {
      'Activity.overdue': {
        Criteria: {
          SearchQuery: {
            Key: {
              $EQ: key,
            },
            StartDate: {
              $LT: '$TODAY()',
            },
          },
        },
        Scope: {
          Fields: this.fields,
        },
      },
    };

    const upcomingRequest: Octopus.ActivityUpcomingReadRequest = {
      'Activity.upcoming': {
        Criteria: {
          SearchQuery: {
            Key: {
              $EQ: key,
            },
            StartDate: {
              $GT: '$TODAY()',
            },
          },
        },
        Scope: {
          Fields: this.fields,
        },
      },
    };

    return this.doAPICall(overdueRequest, upcomingRequest);
  }

  getOverdueAndUpcoming(
    key: string | TimelineKeys,
    type: EntryType,
    filter: TimelineFilter,
    upcomingTop?: number,
  ): Observable<ActivityTimelineUpcomingOverdue> {
    let criteriaOverdue: Octopus.Criteria<Octopus.TimelineHistoric>;
    let criteriaUpcoming: Octopus.Criteria<Octopus.TimelineHistoric>;
    const createCriteria = (
      key?: string | null,
      type?: EntryType,
      showCompleted?: boolean,
      customDateQuery?: Octopus.ComparisonValue,
      ignoreKey = false,
    ) => {
      return key || ignoreKey
        ? this.getCriteria(
            {
              ...filter,
              showCompleted,
              customDateQuery,
            },
            key,
            type,
            undefined,
            ignoreKey,
          ).SearchQuery
        : {};
    };

    if (this.isOpportunityOrCaseModule(type) && isObject(key)) {
      const timelineKeys = key as TimelineKeys;
      const unionCriteriaUpcoming = [];
      const unionCriteriaOverdue = [];

      unionCriteriaOverdue.push(
        createCriteria(
          timelineKeys.abEntryKey ?? null,
          'addressBook',
          false,
          { $LT: '$TODAY()' },
          !timelineKeys.ignoreAbEntry,
        ),
      );
      unionCriteriaUpcoming.push(
        createCriteria(
          timelineKeys.abEntryKey ?? null,
          'addressBook',
          undefined,
          { $GT: '$TODAY()' },
          !timelineKeys.ignoreAbEntry,
        ),
      );

      if (timelineKeys.opportunityKey) {
        unionCriteriaOverdue.push(
          createCriteria(timelineKeys.opportunityKey, 'opportunity', false, {
            $LT: '$TODAY()',
          }),
        );
        unionCriteriaUpcoming.push(
          createCriteria(
            timelineKeys.opportunityKey,
            'opportunity',
            undefined,
            { $GT: '$TODAY()' },
          ),
        );

        if (timelineKeys.leadKey) {
          unionCriteriaOverdue.push(
            createCriteria(timelineKeys.leadKey, 'lead', false, {
              $LT: '$TODAY()',
            }),
          );
          unionCriteriaUpcoming.push(
            createCriteria(timelineKeys.leadKey, 'lead', undefined, {
              $GT: '$TODAY()',
            }),
          );
        }
      } else if (timelineKeys.caseKey) {
        unionCriteriaOverdue.push(
          createCriteria(timelineKeys.caseKey, 'case', false, {
            $LT: '$TODAY()',
          }),
        );
        unionCriteriaUpcoming.push(
          createCriteria(timelineKeys.caseKey, 'case', undefined, {
            $GT: '$TODAY()',
          }),
        );
      }

      criteriaOverdue = {
        SearchQuery: {
          $UNION: unionCriteriaOverdue,
        },
      };
      criteriaUpcoming = {
        SearchQuery: {
          $UNION: unionCriteriaUpcoming,
        },
        Top: upcomingTop,
      };
    } else {
      const singleKey = key as string;
      criteriaOverdue = {
        SearchQuery: createCriteria(singleKey, type, false, {
          $LT: '$TODAY()',
        }),
      };
      criteriaUpcoming = {
        SearchQuery: createCriteria(singleKey, type, undefined, {
          $GT: '$TODAY()',
        }),
        Top: upcomingTop,
      };
    }

    const overdueRequest: Octopus.ActivityOverdueReadRequest = {
      'Activity.overdue': {
        Criteria: criteriaOverdue,
        OrderBy: {
          Fields: [
            {
              StartDate: 'ASC',
            },
          ],
        },
        Scope: {
          Fields: this.fields,
        },
      },
    };
    const upcomingRequest: Octopus.ActivityUpcomingReadRequest = {
      'Activity.upcoming': {
        Criteria: criteriaUpcoming,
        OrderBy: {
          Fields: [
            {
              StartDate: 'ASC',
            },
          ],
        },
        Scope: {
          Fields: this.fields,
        },
      },
    };

    return this.doAPICall(overdueRequest, upcomingRequest);
  }

  private isOpportunityOrCaseModule(entryType: EntryType): boolean {
    return entryType === 'opportunity' || entryType === 'case';
  }

  protected override getFilterQuery({
    interactions,
    categoriesIds,
    showAllCategories,
  }: TimelineFilter): Octopus.LogicalQuery<Octopus.Timeline> {
    const query: Octopus.LogicalQuery<Octopus.Timeline> = {
      $OR: [],
    };

    const hasCategories = !!categoriesIds?.length && !showAllCategories;

    if (hasCategories) {
      const categoryFilter: Octopus.LogicalQuery<Octopus.Timeline> = {
        $AND: [
          {
            InteractionCategory: {
              $IN: categoriesIds,
            },
          },
        ],
      };

      if (interactions?.length) {
        categoryFilter.$AND?.push({
          Type: {
            $IN: interactions,
          },
        });
      }

      query.$OR?.push(categoryFilter);
    } else if (interactions?.length) {
      query.$OR?.push({
        Type: {
          $IN: interactions,
        },
      });
    }
    return query;
  }

  private doAPICall(
    overdueRequest: Octopus.ActivityOverdueReadRequest,
    upcomingRequest: Octopus.ActivityUpcomingReadRequest,
  ) {
    return this.http
      .post<
        Octopus.ActivityOverdueReadResponse &
          Octopus.ActivityUpcomingReadResponse
      >(`${this.context.api}${Octopus.Action.READ}`, {
        ...overdueRequest,
        ...upcomingRequest,
        Compatibility: {
          TaskObject: '2.0',
          ActivityObject: '1.0',
        },
      })
      .pipe(
        map((response) => {
          const mapper = new Octopus.ActivityTimelineMapper();
          return {
            overdue: mapper.from(response['Activity.overdue'].Data),
            upcoming: mapper.from(response['Activity.upcoming'].Data),
          };
        }),
      );
  }
}
